import React, { useState, useEffect, useCallback } from "react"
import DataTable from "components/Tables/DataTable"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Alert,
  Badge,
} from "reactstrap"
import Tooltip from "@mui/material/Tooltip"

import {
  getUsers,
  getProfiles,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  unlockUser,
} from "../../api"

import Layout from "components/Layout"
import toastHandler from "toastHandler"

// BDSJEA-129: ABM de usuarios. Antes esta pantalla era solo una grilla de
// lectura y los perfiles y las claves se tocaban por SQL.

const usuarioVacio = {
  codigo: 0,
  usuario: "",
  nombre: "",
  apellido: "",
  email: "",
  habilitado: true,
  perfiles: [],
}

const estaBloqueado = usuario =>
  !!usuario.bloqueadoHasta && new Date(usuario.bloqueadoHasta) > new Date()

const UserList = () => {
  const [data, setData] = useState([])
  const [perfiles, setPerfiles] = useState([])
  const [loading, setLoading] = useState(false)
  const { setError, setSuccess } = toastHandler()

  const [modalAbierto, setModalAbierto] = useState(false)
  const [esAlta, setEsAlta] = useState(false)
  const [editado, setEditado] = useState(usuarioVacio)
  const [errorForm, setErrorForm] = useState("")
  const [erroresPorCampo, setErroresPorCampo] = useState({})
  const [guardando, setGuardando] = useState(false)

  // La clave provisoria vuelve una sola vez desde el backend. Se muestra en un
  // modal aparte para que el administrador la copie antes de cerrarlo; después
  // no hay forma de recuperarla, solo generar otra.
  const [claveTemporal, setClaveTemporal] = useState(null)

  const cargar = useCallback(() => {
    setLoading(true)
    Promise.all([getUsers(), getProfiles()])
      .then(([usuarios, listaPerfiles]) => {
        setData(usuarios || [])
        setPerfiles((listaPerfiles || []).filter(p => p.vigente))
        setLoading(false)
      })
      .catch(err => {
        setError(err)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    cargar()
  }, [cargar])

  const abrirAlta = () => {
    setEsAlta(true)
    setEditado({ ...usuarioVacio })
    setErrorForm("")
    setErroresPorCampo({})
    setModalAbierto(true)
  }

  const abrirEdicion = usuario => {
    setEsAlta(false)
    setEditado({
      codigo: usuario.codigo,
      usuario: usuario.usuario || "",
      nombre: usuario.nombre || "",
      apellido: usuario.apellido || "",
      email: usuario.email || "",
      habilitado: usuario.habilitado,
      perfiles: usuario.perfiles || [],
    })
    setErrorForm("")
    setErroresPorCampo({})
    setModalAbierto(true)
  }

  const togglePerfil = descripcion => {
    const yaEsta = editado.perfiles.some(p => p.descripcion === descripcion)
    setEditado({
      ...editado,
      perfiles: yaEsta
        ? editado.perfiles.filter(p => p.descripcion !== descripcion)
        : [...editado.perfiles, { descripcion }],
    })
  }

  const guardar = () => {
    setErrorForm("")

    if (!editado.usuario.trim()) {
      setErrorForm("El nombre de usuario es obligatorio.")
      return
    }
    if (editado.perfiles.length === 0) {
      setErrorForm("Asigná al menos un perfil.")
      return
    }

    setGuardando(true)
    const accion = esAlta ? createUser(editado) : updateUser(editado)

    accion
      .then(respuesta => {
        setGuardando(false)
        setModalAbierto(false)
        if (esAlta && respuesta?.claveTemporal) {
          setClaveTemporal({ ...respuesta, email: editado.email })
        } else {
          setSuccess("Usuario actualizado.")
        }
        cargar()
      })
      .catch(err => {
        setErrorForm(err.message)
        setErroresPorCampo(err.erroresPorCampo || {})
        setGuardando(false)
      })
  }

  const confirmarBaja = usuario => {
    if (
      !window.confirm(
        `¿Dar de baja a ${usuario.usuario}? Va a dejar de poder ingresar. ` +
          `El usuario no se borra: queda deshabilitado y se conserva su historial.`,
      )
    ) {
      return
    }
    deleteUser(usuario.codigo)
      .then(() => {
        setSuccess("Usuario dado de baja.")
        cargar()
      })
      .catch(err => setError(err))
  }

  const confirmarReset = usuario => {
    if (
      !window.confirm(
        `¿Generar una clave provisoria para ${usuario.usuario}? ` +
          `La clave actual deja de servir inmediatamente.`,
      )
    ) {
      return
    }
    resetUserPassword(usuario.codigo)
      .then(respuesta => {
        setClaveTemporal({ ...respuesta, email: usuario.email })
        cargar()
      })
      .catch(err => setError(err))
  }

  const desbloquear = usuario => {
    unlockUser(usuario.codigo)
      .then(() => {
        setSuccess(`${usuario.usuario} desbloqueado.`)
        cargar()
      })
      .catch(err => setError(err))
  }

  const columns = [
    { name: "Usuario", selector: row => row.usuario, sortable: true },
    {
      name: "Nombre",
      selector: row => `${row.nombre || ""} ${row.apellido || ""}`,
    },
    { name: "Email", selector: row => row.email || "—", grow: 2 },
    {
      name: "Perfiles",
      selector: row => (
        <Tooltip
          title={(row.perfiles || []).map(p => p.descripcion).join(", ")}
        >
          <div className="badge badge-soft-primary font-size-11 m-1">
            {(row.perfiles || []).map(p => p.descripcion).join(", ") || "—"}
          </div>
        </Tooltip>
      ),
      maxWidth: "300px",
      grow: 2,
    },
    {
      name: "Estado",
      selector: row => {
        if (!row.habilitado)
          return <Badge color="secondary">Deshabilitado</Badge>
        if (estaBloqueado(row)) return <Badge color="danger">Bloqueado</Badge>
        if (row.debeCambiarClave)
          return <Badge color="warning">Clave provisoria</Badge>
        return <Badge color="success">Activo</Badge>
      },
    },
    {
      name: "Acciones",
      selector: row => (
        <div className="d-flex gap-1">
          <Button size="sm" color="light" onClick={() => abrirEdicion(row)}>
            Editar
          </Button>
          <Button size="sm" color="light" onClick={() => confirmarReset(row)}>
            Resetear clave
          </Button>
          {estaBloqueado(row) ? (
            <Button size="sm" color="warning" onClick={() => desbloquear(row)}>
              Desbloquear
            </Button>
          ) : null}
          {row.habilitado ? (
            <Button size="sm" color="light" onClick={() => confirmarBaja(row)}>
              Baja
            </Button>
          ) : null}
        </div>
      ),
      grow: 3,
    },
  ]

  const customStyles = {
    table: { style: { padding: "0px 20px" } },
    rows: {
      style: {
        minHeight: "50px",
        fontWeight: 400,
        fontSize: "13px",
        textOverflow: "clip",
      },
      highlightOnHoverStyle: {
        backgroundColor: "rgb(230, 244, 244)",
        borderBottomColor: "#FFFFFF",
        borderRadius: "25px",
        outline: "1px solid #FFFFFF",
      },
    },
    headCells: {
      style: { fontSize: "13px", fontWeight: 600, color: "rgb(74, 74, 74)" },
    },
  }

  return (
    <React.Fragment>
      <Layout
        title="Usuarios"
        save={false}
        loading={loading}
        components={[
          {
            component: (
              <div>
                <div className="d-flex justify-content-end p-3">
                  <Button color="primary" onClick={abrirAlta}>
                    Nuevo usuario
                  </Button>
                </div>
                <DataTable
                  columns={columns}
                  data={data}
                  styles={customStyles}
                  dense={false}
                  pagination={true}
                  showAll={true}
                />
              </div>
            ),
            styles: "p-0",
          },
        ]}
      />

      <Modal isOpen={modalAbierto} toggle={() => setModalAbierto(false)}>
        <ModalHeader toggle={() => setModalAbierto(false)}>
          {esAlta ? "Nuevo usuario" : `Editar ${editado.usuario}`}
        </ModalHeader>
        <ModalBody>
          {errorForm ? <Alert color="danger">{errorForm}</Alert> : null}
          <Form>
            <FormGroup>
              <Label>Usuario</Label>
              <Input
                value={editado.usuario}
                // El username identifica al usuario en el log de auditoría y en
                // el historial de claves: cambiarlo rompería esa trazabilidad.
                disabled={!esAlta}
                onChange={e =>
                  setEditado({ ...editado, usuario: e.target.value })
                }
                invalid={!!erroresPorCampo.usuario || (!!errorForm && !editado.usuario.trim())}
              />
              {erroresPorCampo.usuario ? (
                <FormFeedback>{erroresPorCampo.usuario}</FormFeedback>
              ) : !esAlta ? (
                <small className="text-muted">
                  El nombre de usuario no se puede modificar.
                </small>
              ) : null}
            </FormGroup>
            <FormGroup>
              <Label>Nombre</Label>
              <Input
                value={editado.nombre}
                onChange={e =>
                  setEditado({ ...editado, nombre: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Apellido</Label>
              <Input
                value={editado.apellido}
                onChange={e =>
                  setEditado({ ...editado, apellido: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={editado.email}
                onChange={e =>
                  setEditado({ ...editado, email: e.target.value })
                }
                invalid={!!erroresPorCampo.email}
              />
              {erroresPorCampo.email ? (
                <FormFeedback>{erroresPorCampo.email}</FormFeedback>
              ) : (
                <small className="text-muted">
                  Sin mail, el usuario no recibe los avisos de clave provisoria,
                  cambio de clave ni bloqueo de cuenta.
                </small>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Perfiles</Label>
              {perfiles.map(perfil => (
                <div className="form-check" key={perfil.codigo}>
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id={`perfil-${perfil.codigo}`}
                    checked={editado.perfiles.some(
                      p => p.descripcion === perfil.descripcion,
                    )}
                    onChange={() => togglePerfil(perfil.descripcion)}
                  />
                  <Label
                    className="form-check-label"
                    for={`perfil-${perfil.codigo}`}
                  >
                    {perfil.descripcion}
                  </Label>
                </div>
              ))}
            </FormGroup>
            {!esAlta ? (
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="habilitado"
                  checked={editado.habilitado}
                  onChange={e =>
                    setEditado({ ...editado, habilitado: e.target.checked })
                  }
                />
                <Label className="form-check-label" for="habilitado">
                  Habilitado
                </Label>
              </FormGroup>
            ) : (
              <Alert color="info" className="mb-0">
                Al crearlo se genera una clave provisoria. Se muestra una sola
                vez y el usuario tiene que cambiarla al ingresar.
              </Alert>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModalAbierto(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={guardar} disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!claveTemporal} toggle={() => setClaveTemporal(null)}>
        <ModalHeader toggle={() => setClaveTemporal(null)}>
          Clave provisoria
        </ModalHeader>
        <ModalBody>
          <p>
            Clave para <strong>{claveTemporal?.usuario}</strong>:
          </p>
          <pre className="p-3 bg-light border rounded fs-5 mb-3">
            {claveTemporal?.claveTemporal}
          </pre>
          {/* Se muestra siempre, tenga mail o no: si el SMTP falla, el mail se
              pierde en silencio y el administrador tiene que poder entregarla. */}
          {claveTemporal?.email ? (
            <Alert color="info" className="mb-0">
              Se envió por mail a <strong>{claveTemporal.email}</strong>. Si no
              le llega, copiala y entregásela por otro medio: no se puede volver
              a consultar. La va a tener que cambiar en el primer ingreso.
            </Alert>
          ) : (
            <Alert color="warning" className="mb-0">
              El usuario no tiene mail cargado, así que no se envió ninguna
              notificación. Copiala ahora y entregásela por un canal fuera de la
              aplicación: no se puede volver a consultar. La va a tener que
              cambiar en el primer ingreso.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() =>
              navigator.clipboard?.writeText(claveTemporal?.claveTemporal || "")
            }
          >
            Copiar
          </Button>
          <Button color="primary" onClick={() => setClaveTemporal(null)}>
            Listo
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default UserList
